import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Slider from 'rc-slider';
import NumericInput from 'react-numeric-input';
import CodeEditor from '@uiw/react-textarea-code-editor';
import RichText from '../controls/RichText';
import { useForceUpdate, numberToLetter } from '../util';
import { Content, deepMap, hydrate } from '../DataStructures';
import { TabContextReporter } from '../TabContext';
import { FormValidityReporter } from '../FormValidity';
import { isContentEmpty } from '../util';
import { TaskSlider, TaskGraphSliderConfigurable } from '@bittnerdevelopmentgroup/interactive';
import "../App.css";
import InteractiveRenderer from '../InteractiveRenderer';
import { TwitterPicker } from 'react-color';
import TooltipSlider, { handleRender } from '../controls/TooltipSlider';
console.log("slider", Slider);
function getEquationError(variables, axis, equation, dataProps) {
  try {
    new Function(...variables, axis, "return " + equation)(variables.map((_, sliderid) => {
      return TaskGraphSliderConfigurable.sliderValueToGraphValue(dataProps.sliders[sliderid].initialPosition, dataProps.trackRanges[sliderid], dataProps.roundValues);
    }), 0);
    return null;
  } catch (e) {
    return e.toString();
  }
}
export default function (props) {
  const forceUpdate = useForceUpdate();
  const dataProps = props.data.props;
  let errorRanges = [];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TabContextReporter, {
    index: "0",
    title: "1. Initial Information"
  }, /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Number of Sliders:"), " ", /*#__PURE__*/React.createElement("i", null, "How many variables are controllable by the user?")), /*#__PURE__*/React.createElement(NumericInput, {
    min: 1,
    max: 4,
    value: dataProps.sliders.length,
    onChange: v => {
      if (dataProps.sliders.length === 0) {
        dataProps.sliders.push({
          initialPosition: 50,
          label: new Content("Variable alpha"),
          ariaLabelForHandle: "Choose a value for variable alpha."
        });
      }
      if (dataProps.trackRanges.length === 0) {
        dataProps.trackRanges.push([{
          min: {
            label: /*#__PURE__*/React.createElement(React.Fragment, null, "1/2"),
            value: 0.5
          },
          max: 1,
          minAnchor: 0,
          maxAnchor: 50
        }, {
          min: 1,
          max: 2,
          minAnchor: 50,
          maxAnchor: 100
        }]);
      }
      while (v > dataProps.sliders.length) {
        dataProps.sliders.push(deepMap(JSON.parse(JSON.stringify(dataProps.sliders[dataProps.sliders.length - 1])), hydrate));
        dataProps.trackRanges.push(deepMap(JSON.parse(JSON.stringify(dataProps.trackRanges[dataProps.trackRanges.length - 1])), hydrate));
        for (const text of dataProps.texts) {
          text.sliderRanges.push(deepMap(JSON.parse(JSON.stringify(text.sliderRanges[text.sliderRanges.length - 1])), hydrate));
        }
        for (const text of dataProps.texts) {
          text.sliderPositions.push(deepMap(JSON.parse(JSON.stringify(text.sliderPositions[text.sliderPositions.length - 1])), hydrate));
        }
      }
      if (v < dataProps.sliders.length && v > 0) {
        dataProps.sliders.length = v;
        dataProps.trackRanges.length = v;
        for (const text of dataProps.texts) {
          text.sliderRanges.length = v;
        }
        for (const text of dataProps.texts) {
          text.sliderPositions.length = v;
        }
      }
      props.onChanged();
    }
  }), /*#__PURE__*/React.createElement(FormValidityReporter, {
    valid: dataProps.sliders.length > 0
  }, "You must have at least one slider.")), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Number of Graphs:"), " ", /*#__PURE__*/React.createElement("i", null, "How many graphs should there be?")), /*#__PURE__*/React.createElement(NumericInput, {
    min: 1,
    max: 4,
    value: dataProps.graphs.length,
    onChange: v => {
      if (dataProps.graphs.length === 0) {
        dataProps.graphs.push({
          canvasAltText: "Graph of slope-intercept form",
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            scales: {
              x: {
                min: -15,
                max: 15,
                title: {
                  text: "x"
                },
                tickInterval: 5
              },
              y: {
                min: 0,
                max: 30,
                title: {
                  text: "y"
                },
                tickInterval: 5
              }
            }
          }
        });
      }
      while (v > dataProps.graphs.length) {
        dataProps.graphs.push(deepMap(JSON.parse(JSON.stringify(dataProps.graphs[dataProps.graphs.length - 1])), hydrate));
        dataProps.equations.push(deepMap(JSON.parse(JSON.stringify(dataProps.equations[dataProps.equations.length - 1])), hydrate));
        dataProps.pointSets.push(deepMap(JSON.parse(JSON.stringify(dataProps.pointSets[dataProps.pointSets.length - 1])), hydrate));
      }
      if (v < dataProps.graphs.length && v > 0) {
        dataProps.graphs.length = v;
        dataProps.equations.length = v;
        dataProps.pointSets.length = v;
      }
      props.onChanged();
    }
  }), /*#__PURE__*/React.createElement(FormValidityReporter, {
    valid: dataProps.graphs.length > 0
  }, "You must have at least one slider.")), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Number of Descriptions:"), " ", /*#__PURE__*/React.createElement("i", null, "How many descriptions for different combinations of values should there be?")), /*#__PURE__*/React.createElement(NumericInput, {
    min: 1,
    max: 4,
    value: dataProps.texts.length,
    onChange: v => {
      if (dataProps.texts.length === 0) {
        dataProps.texts.push({
          sliderRanges: [[-4, -0.01], [0, 15]],
          text: new Content("At slopes less than zero, the x-intercept is positive."),
          altText: "As the slope gets more negative, the line's x-intercept gets more positive."
        });
      }
      while (v > dataProps.texts.length) {
        dataProps.texts.push(deepMap(JSON.parse(JSON.stringify(dataProps.texts[dataProps.texts.length - 1])), hydrate));
      }
      if (v < dataProps.texts.length && v > 0) {
        dataProps.texts.length = v;
      }
      props.onChanged();
    }
  }), /*#__PURE__*/React.createElement(FormValidityReporter, {
    valid: dataProps.texts.length > 0
  }, "You must have at least one description.")))), /*#__PURE__*/React.createElement(TabContextReporter, {
    index: "1",
    title: "2. Slider configuration"
  }, dataProps.sliders.map((slider, sliderid) => {
    var _errorRanges$sliderid;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: sliderid
    }, /*#__PURE__*/React.createElement("h2", null, "Variable ", numberToLetter(sliderid)), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Initial Position:"), " ", /*#__PURE__*/React.createElement("i", null, "Where does the slider handle start?")), /*#__PURE__*/React.createElement(TaskSlider, {
      min: 0,
      max: 100,
      step: 1,
      value: slider.initialPosition,
      handleChange: v => {
        slider.initialPosition = v;
        props.onChanged();
      }
    })), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Label:"), " ", /*#__PURE__*/React.createElement("i", null, "User label for slider.")), /*#__PURE__*/React.createElement(RichText, {
      inlineOnly: true,
      value: slider.label,
      onChange: v => {
        slider.label = v;
        props.onChanged();
      }
    }), /*#__PURE__*/React.createElement(FormValidityReporter, {
      valid: dataProps.texts.length > 0
    }, "You must have at least one description.")), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Alt Text:"), " ", /*#__PURE__*/React.createElement("i", null, "Screen reader label for slider handle.")), /*#__PURE__*/React.createElement(Form.Control, {
      as: "textarea",
      value: slider.ariaLabelForHandle,
      onChange: e => {
        slider.ariaLabelForHandle = e.target.value;
        props.onChanged();
      }
    }), /*#__PURE__*/React.createElement(FormValidityReporter, {
      valid: dataProps.texts.length > 0
    }, "You must have at least one description.")), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Number of Value Mappings:"), " ", /*#__PURE__*/React.createElement("i", null, "How many segments should the slider be broken into?")), /*#__PURE__*/React.createElement(NumericInput, {
      min: 1,
      max: 4,
      value: dataProps.trackRanges[sliderid].length,
      onChange: v => {
        if (dataProps.trackRanges[sliderid].length === 0) {
          dataProps.trackRanges[sliderid].push({
            min: {
              label: new Content("-4"),
              value: -4
            },
            max: {
              label: new Content("4"),
              value: 4
            },
            minAnchor: 0,
            maxAnchor: 100
          });
        }
        while (v > dataProps.trackRanges[sliderid].length) {
          dataProps.trackRanges[sliderid].push(deepMap(JSON.parse(JSON.stringify(dataProps.trackRanges[sliderid][dataProps.trackRanges[sliderid].length - 1])), hydrate));
        }
        if (v < dataProps.trackRanges[sliderid].length && v > 0) {
          dataProps.trackRanges[sliderid].length = v;
        }
        props.onChanged();
      }
    }), /*#__PURE__*/React.createElement(FormValidityReporter, {
      valid: dataProps.trackRanges[sliderid].length > 0
    }, "You must have at least one slider.")), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Value Mapping:"), " ", /*#__PURE__*/React.createElement("i", null, "Map different portions of the slider to different value ranges.")), dataProps.trackRanges[sliderid].map(({
      min,
      max,
      minAnchor,
      maxAnchor
    }, rangeid) => /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(TooltipSlider, {
      range: true,
      value: [minAnchor, maxAnchor],
      onChange: v => {
        dataProps.trackRanges[sliderid][rangeid].minAnchor = v[0];
        dataProps.trackRanges[sliderid][rangeid].maxAnchor = v[1];
        props.onChanged();
      }
    }), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(NumericInput, {
      precision: 2,
      value: min.value,
      onChange: v => {
        min.value = v;
        props.onChanged();
      }
    }), /*#__PURE__*/React.createElement(RichText, {
      inlineOnly: true,
      value: min.label,
      onChange: v => {
        min.label = v;
        props.onChanged();
      }
    })), /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(NumericInput, {
      precision: 2,
      value: max.value,
      onChange: v => {
        max.value = v;
        props.onChanged();
      }
    }), /*#__PURE__*/React.createElement(RichText, {
      inlineOnly: true,
      value: max.label,
      onChange: v => {
        max.label = v;
        props.onChanged();
      }
    }))))), /*#__PURE__*/React.createElement(FormValidityReporter, {
      valid: new Array(101).fill(0).map((_, i) => {
        for (const range of dataProps.trackRanges[sliderid]) {
          if (range.minAnchor <= i && range.maxAnchor >= i) {
            return true;
          }
        }
        if (errorRanges[sliderid] === undefined) {
          errorRanges[sliderid] = [[i, i]];
        }
        if (errorRanges[sliderid][errorRanges[sliderid].length - 1][1] === i || errorRanges[sliderid][errorRanges[sliderid].length - 1][1] === i - 1) {
          errorRanges[sliderid][errorRanges[sliderid].length - 1][1] = i;
        } else {
          errorRanges[sliderid].push([i, i]);
        }
        return false;
      }).every(v => v)
    }, "Portions of the slider are missing value mappings:", /*#__PURE__*/React.createElement("ul", null, (_errorRanges$sliderid = errorRanges[sliderid]) === null || _errorRanges$sliderid === void 0 ? void 0 : _errorRanges$sliderid.map(range => /*#__PURE__*/React.createElement("li", null, range[0] === range[1] ? range[0] : `${range[0]}-${range[1]}`, "%")))))));
  })), /*#__PURE__*/React.createElement(TabContextReporter, {
    index: "2",
    title: "3. Graph configuration"
  }, "Documentation: ", /*#__PURE__*/React.createElement("a", {
    href: "https://www.chartjs.org/docs/latest/configuration/"
  }, "https://www.chartjs.org/docs/latest/configuration/"), dataProps.graphs.map((graph, graphid) => {
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: graphid
    }, /*#__PURE__*/React.createElement("h2", null, "Graph ", graphid + 1), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(CodeEditor, {
      language: "json",
      value: JSON.stringify(graph, undefined, 4),
      onChange: e => {
        try {
          dataProps.graphs[graphid] = JSON.parse(e.target.value);
          props.onChanged();
        } catch (e) {}
      }
    })));
  })), /*#__PURE__*/React.createElement(TabContextReporter, {
    index: "3",
    title: "4. Equation configuration"
  }, dataProps.graphs.map((graph, graphid) => {
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: graphid
    }, /*#__PURE__*/React.createElement("h2", null, "Graph ", graphid + 1), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Number of Equations:"), " ", /*#__PURE__*/React.createElement("i", null, "How many equations should there be?")), /*#__PURE__*/React.createElement(NumericInput, {
      min: 1,
      max: 4,
      value: dataProps.equations[graphid].length,
      onChange: v => {
        if (dataProps.equations[graphid].length === 0) {
          dataProps.equations[graphid].push({
            label: 'eq1',
            yEquals: 'a * x + b',
            borderColor: '#595959',
            borderDash: [10, 5]
          });
        }
        while (v > dataProps.equations[graphid].length) {
          dataProps.equations[graphid].push(JSON.parse(JSON.stringify(dataProps.equations[graphid][dataProps.equations[graphid].length - 1])));
        }
        if (v < dataProps.equations[graphid].length && v > 0) {
          dataProps.equations[graphid].length = v;
        }
        props.onChanged();
      }
    })), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Number of Points:"), " ", /*#__PURE__*/React.createElement("i", null, "How many points should there be?")), /*#__PURE__*/React.createElement(NumericInput, {
      min: 1,
      max: 4,
      value: dataProps.pointSets[graphid][0].length,
      onChange: v => {
        if (dataProps.pointSets[graphid][0].length === 0) {
          dataProps.pointSets[graphid][0] = [{
            xEquals: '0',
            yEquals: 'b'
          }, {
            xEquals: '-b / a',
            yEquals: '0'
          }];
        }
        while (v > dataProps.pointSets[graphid][0].length) {
          dataProps.pointSets[graphid][0].push(JSON.parse(JSON.stringify(dataProps.pointSets[graphid][0][dataProps.pointSets[graphid][0].length - 1])));
        }
        if (v < dataProps.pointSets[graphid][0].length && v > 0) {
          dataProps.pointSets[graphid][0].length = v;
        }
        props.onChanged();
      }
    }))), /*#__PURE__*/React.createElement("h2", null, "Equations"), dataProps.equations[graphid].map((equation, equationid) => {
      let variables = dataProps.sliders.map((_, sliderid) => {
        return String.fromCharCode(97 + sliderid);
      });
      return /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement("h4", null, "Equation ", equationid + 1), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Y Equals:"), " ", /*#__PURE__*/React.createElement("i", null, "Variables are: ", dataProps.sliders.map((slider, sliderid) => {
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: sliderid
        }, /*#__PURE__*/React.createElement("code", null, variables[sliderid]), " = ", /*#__PURE__*/React.createElement(InteractiveRenderer, {
          data: slider.label
        }), ". ");
      }))), /*#__PURE__*/React.createElement(Form.Control, {
        as: "textarea",
        value: equation.yEquals,
        onChange: e => {
          equation.yEquals = e.target.value;
          if (!getEquationError(variables, "x", equation.yEquals, dataProps)) {
            props.onChanged();
          } else {
            forceUpdate();
          }
        }
      }), /*#__PURE__*/React.createElement(FormValidityReporter, {
        valid: !getEquationError(variables, "x", equation.yEquals, dataProps)
      }, getEquationError(variables, "x", equation.yEquals, dataProps))), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Color:"), " ", /*#__PURE__*/React.createElement("i", null, "Color for the equation.")), /*#__PURE__*/React.createElement(TwitterPicker, {
        color: equation.borderColor,
        onChange: c => {
          equation.borderColor = c.hex;
          props.onChanged();
        },
        triangle: "hide",
        colors: ['#FF6900', '#FCB900', '#7BDCB5', '#00D084', '#8ED1FC', '#0693E3', '#ABB8C3', '#EB6F8F', '#F78DA7', '#DABEEF']
      })));
    }), /*#__PURE__*/React.createElement("h2", null, "Points"), dataProps.pointSets[graphid][0].map((point, pointid) => {
      let variables = dataProps.sliders.map((_, sliderid) => {
        return String.fromCharCode(97 + sliderid);
      });
      return /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement("h4", null, "Point ", pointid + 1), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "X Equals:"), " ", /*#__PURE__*/React.createElement("i", null, "Variables are: ", dataProps.sliders.map((slider, sliderid) => {
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: sliderid
        }, /*#__PURE__*/React.createElement("code", null, variables[sliderid]), " = ", /*#__PURE__*/React.createElement(InteractiveRenderer, {
          data: slider.label
        }), ". ");
      }))), /*#__PURE__*/React.createElement(Form.Control, {
        as: "textarea",
        value: point.xEquals,
        onChange: e => {
          point.xEquals = e.target.value;
          if (!getEquationError(variables, "y", point.xEquals, dataProps)) {
            props.onChanged();
          } else {
            forceUpdate();
          }
        }
      }), /*#__PURE__*/React.createElement(FormValidityReporter, {
        valid: !getEquationError(variables, "y", point.xEquals, dataProps)
      }, getEquationError(variables, "y", point.xEquals, dataProps))), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Y Equals:"), " ", /*#__PURE__*/React.createElement("i", null, "Variables are: ", dataProps.sliders.map((slider, sliderid) => {
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: sliderid
        }, /*#__PURE__*/React.createElement("code", null, variables[sliderid]), " = ", /*#__PURE__*/React.createElement(InteractiveRenderer, {
          data: slider.label
        }), ". ");
      }))), /*#__PURE__*/React.createElement(Form.Control, {
        as: "textarea",
        value: point.yEquals,
        onChange: e => {
          point.yEquals = e.target.value;
          if (!getEquationError(variables, "x", point.yEquals, dataProps)) {
            props.onChanged();
          } else {
            forceUpdate();
          }
        }
      }), /*#__PURE__*/React.createElement(FormValidityReporter, {
        valid: !getEquationError(variables, "x", point.yEquals, dataProps)
      }, getEquationError(variables, "x", point.yEquals, dataProps))));
    }));
  })), /*#__PURE__*/React.createElement(TabContextReporter, {
    index: "4",
    title: "5. Text configuration"
  }, dataProps.texts.map((text, textid) => {
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: textid
    }, /*#__PURE__*/React.createElement("h2", null, "Text ", textid + 1), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Active region:"), " ", /*#__PURE__*/React.createElement("i", null, "What regions of the sliders should this text be active on? Only the first-matching text will be displayed.")), dataProps.sliders.map((slider, sliderid) => {
      var _text$sliderPositions, _text$sliderPositions2;
      text.sliderPositions = (_text$sliderPositions = text.sliderPositions) !== null && _text$sliderPositions !== void 0 ? _text$sliderPositions : [];
      text.sliderPositions[sliderid] = (_text$sliderPositions2 = text.sliderPositions[sliderid]) !== null && _text$sliderPositions2 !== void 0 ? _text$sliderPositions2 : [0, 100];
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: sliderid
      }, /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, "Variable ", numberToLetter(sliderid)), /*#__PURE__*/React.createElement(TooltipSlider, {
        range: true,
        value: text.sliderPositions && text.sliderPositions[sliderid],
        onChange: v => {
          text.sliderRanges[sliderid][0] = TaskGraphSliderConfigurable.sliderValueToGraphValue(v[0], dataProps.trackRanges[sliderid], dataProps.roundValues);
          text.sliderRanges[sliderid][1] = TaskGraphSliderConfigurable.sliderValueToGraphValue(v[1], dataProps.trackRanges[sliderid], dataProps.roundValues);
          text.sliderPositions[sliderid] = v;
          props.onChanged();
        }
      })));
    })), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Display text:"), " ", /*#__PURE__*/React.createElement("i", null, "Text to be displayed underneath the graph.")), /*#__PURE__*/React.createElement(RichText, {
      value: text.text,
      onChange: v => {
        text.text = v;
        props.onChanged();
      }
    })), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Label, null, /*#__PURE__*/React.createElement("b", null, "Graph description:"), " ", /*#__PURE__*/React.createElement("i", null, "Screen reader description for the graph.")), /*#__PURE__*/React.createElement(Form.Control, {
      as: "textarea",
      value: text.altText,
      onChange: e => {
        text.altText = e.target.value;
        props.onChanged();
      }
    }))));
  })));
}